import React, { memo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { PhoneCall, Bot, User, ChevronDown, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import { contactsAPI } from '../../api';

const LogCallDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  contactPhone,
  teamMembers = [], 
  aiAgents = [],
  onSuccess 
}) => {
  const [form, setForm] = useState({
    direction: 'outbound',
    outcome: 'connected',
    duration_minutes: '',
    notes: ''
  });
  const [createFollowUp, setCreateFollowUp] = useState(false);
  const [followUpDetails, setFollowUpDetails] = useState({
    title: '',
    due_date: '',
    assigned_to: '',
    assigned_type: 'user'
  });
  const [loading, setLoading] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      
      await contactsAPI.createCall(contactId, {
        ...form,
        phone_number: contactPhone,
        contact_id: contactId,
        duration_minutes: parseInt(form.duration_minutes) || 0
      });
      
      // Create follow-up task if requested
      if (createFollowUp && followUpDetails.title) {
        let assignedToName = '';
        if (followUpDetails.assigned_type === 'ai_agent') {
          const agent = aiAgents.find(a => a.id === followUpDetails.assigned_to);
          assignedToName = agent?.name || agent?.display_name || 'AI Agent';
        } else {
          const member = teamMembers.find(m => m.id === followUpDetails.assigned_to);
          assignedToName = member ? `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email : '';
        }
        
        const taskData = {
          title: followUpDetails.title,
          description: `Follow-up for call: ${form.outcome} - ${form.notes?.substring(0, 100) || 'No notes'}`,
          priority: 'medium',
          status: 'pending',
          due_date: followUpDetails.due_date ? new Date(followUpDetails.due_date).toISOString() : null,
          assigned_to: followUpDetails.assigned_to || null,
          assigned_to_type: followUpDetails.assigned_type,
          assigned_to_name: assignedToName,
          is_follow_up: true
        };
        await contactsAPI.createTask(contactId, taskData);
        toast.success('Call logged with follow-up task created');
      } else {
        toast.success('Call logged');
      }
      
      // Reset form
      setForm({ direction: 'outbound', outcome: 'connected', duration_minutes: '', notes: '' });
      setCreateFollowUp(false);
      setFollowUpDetails({ title: '', due_date: '', assigned_to: '', assigned_type: 'user' });
      
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to log call');
    } finally {
      setLoading(false);
    }
  }, [form, contactId, contactPhone, createFollowUp, followUpDetails, teamMembers, aiAgents, onOpenChange, onSuccess]);

  const handleClose = useCallback(() => {
    setForm({ direction: 'outbound', outcome: 'connected', duration_minutes: '', notes: '' });
    setCreateFollowUp(false);
    setFollowUpDetails({ title: '', due_date: '', assigned_to: '', assigned_type: 'user' });
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="h-5 w-5 text-green-600" />
            Log Call
          </DialogTitle>
          <DialogDescription>
            Record a phone call with this contact
            {contactPhone && <span className="block mt-1 font-medium">{contactPhone}</span>}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Direction</Label>
              <Select 
                value={form.direction} 
                onValueChange={(val) => setForm(prev => ({ ...prev, direction: val }))}
              >
                <SelectTrigger data-testid="call-direction-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="outbound">Outbound</SelectItem>
                  <SelectItem value="inbound">Inbound</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Outcome</Label>
              <Select 
                value={form.outcome} 
                onValueChange={(val) => setForm(prev => ({ ...prev, outcome: val }))}
              >
                <SelectTrigger data-testid="call-outcome-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="connected">Connected</SelectItem>
                  <SelectItem value="voicemail">Voicemail</SelectItem>
                  <SelectItem value="no_answer">No Answer</SelectItem>
                  <SelectItem value="busy">Busy</SelectItem>
                  <SelectItem value="wrong_number">Wrong Number</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="call-duration">Duration (minutes)</Label>
            <Input
              id="call-duration"
              type="number"
              min="0"
              value={form.duration_minutes}
              onChange={(e) => setForm(prev => ({ ...prev, duration_minutes: e.target.value }))}
              placeholder="0"
              data-testid="call-duration-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="call-notes">Notes</Label>
            <Textarea
              id="call-notes"
              value={form.notes}
              onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Call notes and key points discussed..."
              rows={4}
              data-testid="call-notes-input"
            />
          </div>
          
          {/* Follow-up Task Section */}
          <Collapsible open={followUpOpen} onOpenChange={setFollowUpOpen}>
            <CollapsibleTrigger asChild>
              <Button type="button" variant="ghost" className="w-full justify-between p-2 h-auto">
                <div className="flex items-center gap-2">
                  <CheckSquare className="h-4 w-4" />
                  <span className="text-sm">Create Follow-up Task</span>
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform ${followUpOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-2">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="create-call-followup"
                  checked={createFollowUp}
                  onCheckedChange={setCreateFollowUp}
                />
                <Label htmlFor="create-call-followup" className="text-sm">Create a follow-up task</Label>
              </div>
              
              {createFollowUp && (
                <div className="space-y-3 pl-6 border-l-2 border-green-200">
                  <Input
                    value={followUpDetails.title}
                    onChange={(e) => setFollowUpDetails(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Follow-up task title"
                    data-testid="call-followup-title-input"
                  />
                  <Input
                    type="date"
                    value={followUpDetails.due_date}
                    onChange={(e) => setFollowUpDetails(prev => ({ ...prev, due_date: e.target.value }))}
                    data-testid="call-followup-date-input"
                  />
                  
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Assignment Type</Label>
                    <Select 
                      value={followUpDetails.assigned_type}
                      onValueChange={(val) => setFollowUpDetails(prev => ({ ...prev, assigned_type: val, assigned_to: '' }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Assign to..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-blue-600" /> Team Member (Human)
                          </div>
                        </SelectItem>
                        <SelectItem value="ai_agent">
                          <div className="flex items-center gap-2">
                            <Bot className="h-4 w-4 text-purple-600" /> AI Agent
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {followUpDetails.assigned_type === 'user' ? (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        <User className="h-3 w-3" /> Select Team Member
                      </Label>
                      <Select 
                        value={followUpDetails.assigned_to}
                        onValueChange={(val) => setFollowUpDetails(prev => ({ ...prev, assigned_to: val }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose team member..." />
                        </SelectTrigger>
                        <SelectContent>
                          {teamMembers.map(member => (
                            <SelectItem key={member.id} value={member.id}>
                              {member.first_name} {member.last_name} {member.email ? `(${member.email})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground flex items-center gap-1">
                        <Bot className="h-3 w-3" /> Select AI Agent
                      </Label>
                      <Select 
                        value={followUpDetails.assigned_to}
                        onValueChange={(val) => setFollowUpDetails(prev => ({ ...prev, assigned_to: val }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose AI agent..." />
                        </SelectTrigger>
                        <SelectContent>
                          {aiAgents.map(agent => (
                            <SelectItem key={agent.id} value={agent.id}>
                              {agent.name || agent.display_name} - {agent.role_title || 'AI Assistant'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="log-call-submit">
              {loading ? 'Logging...' : 'Log Call'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

LogCallDialog.displayName = 'LogCallDialog';

export default LogCallDialog;
